import React, { FC, useCallback, useState } from "react";

import { generateFollowThroughPattern } from "@xapp/arachne-utils";

import {
    UpdateAppInput,
    useAnalyzeWebsiteMutation,
    UserProfile,
    useStartCrawlMutation,
    useUpdateApplicationMutation,
} from "generated/graphql";

import { wrapUrqlMutation } from "utils/graphql";
import { useBoolState } from "utils/bool";
import { cleanObj } from "utils/Json";

import { WebsiteQuestionStepProps, WebsiteQuestionStep } from "../components/WebsiteQuestionStep";
import { OnboardingProps } from "../models";

export interface WebsiteQuestionStepContainerProps extends Omit<WebsiteQuestionStepProps, "onNext"> {
    /**
     * Site data used to prepopulate fields
     */
    initialProps?: OnboardingProps;
    /**
     * Profile of the user
     */
    profile: UserProfile;
    /**
     * Selected product
     */
    product: string;
    /**
     * The user is ready for the next step
     */
    onNextStep(props: OnboardingProps): void;
}

export const WebsiteQuestionStepContainer: FC<WebsiteQuestionStepContainerProps> = props => {

    const { onNextStep, initialProps } = props;

    const [creationStatusMessage, setCreationStatusMessage] = useState<string>();
    const [creating, stopCreating, startCreating] = useBoolState(false);
    const [error, setError] = useState<{ error: string, message: string }>();

    const updateApplication = wrapUrqlMutation(useUpdateApplicationMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating the contact information on the app for you." });
            return { send: false }
        }
    });

    const startCrawl = wrapUrqlMutation(useStartCrawlMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble starting the website crawl for you." });
            return { send: false }
        }
    });

    const analyzeSite = wrapUrqlMutation(useAnalyzeWebsiteMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean, message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble starting the website crawl for you." });
            return { send: false }
        }
    });

    /**
     * If they have a website, start the crawl & analysis, then move on
     */
    const handleNext = useCallback(async (hasWebsite: boolean, website?: string) => {
        startCreating();

        const appProps = cleanObj(initialProps);

        appProps.beta = {
            ...appProps.beta,
            onboarding: {
                ...appProps.beta?.onboarding,
                hasWebsite
            }
        };

        const updateAppProps: UpdateAppInput = {
            beta: {
                ...appProps.beta,
                onboarding: {
                    ...appProps.beta?.onboarding,
                    hasWebsite,
                    lastStep: "website"
                }
            },
        }

        // if they have a website, start crawling & analysis
        if (website) {

            appProps.website = website;
            updateAppProps.website = website;

            // Start Crawl
            setCreationStatusMessage("Starting ingestion...");
            await new Promise(r => setTimeout(r, 200));

            const pattern: string[] = [generateFollowThroughPattern(appProps.website)];

            await startCrawl({
                appId: appProps.appId,
                url: appProps.website,
                pattern,
                channelId: appProps.chatWidgetId || ""
            });

            setCreationStatusMessage("Starting website analysis...");
            await new Promise(r => setTimeout(r, 200));
            const result = await analyzeSite({ url: appProps.website }).catch((e) => {
                console.error("Failed to analyze site", e);
            });

            if (result) {
                const executionId = result?.data?.onboarding?.startRetrieveWebsiteDetails?.executionId;
                console.log('got executionId', executionId);
                if (executionId) {
                    appProps.executionId = executionId;
                    updateAppProps.websiteData = {
                        crawlExecutionId: executionId
                    }
                }
            }
        }

        // Update the app with the website
        setCreationStatusMessage("Updating business information...");
        await new Promise(r => setTimeout(r, 200));

        console.log('updating app', updateAppProps);

        await updateApplication({
            appId: appProps.appId,
            app: updateAppProps
        });

        // Update the current fields in case of an error
        sessionStorage.setItem("onboarding-appProps", JSON.stringify(appProps));

        stopCreating();

        // Move on!
        onNextStep(appProps);
    }, [analyzeSite, initialProps, onNextStep, startCrawl, startCreating, stopCreating, updateApplication]);

    return (
        <WebsiteQuestionStep
            {...props}
            analyzing={creating}
            analyzingStatusMessage={creationStatusMessage}
            onNext={handleNext}
            error={error}
        />
    );
}